import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
} from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../../lib/cn";

function DialogContent({
  className,
  title,
  description,
  children,
  footer,
}: {
  className?: string;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Portal>
      <Overlay
        className={cn(
          "fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in data-[state=closed]:fade-out",
        )}
      />
      <Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[min(92vw,32rem)] -translate-x-1/2 -translate-y-1/2",
          // Cap at the viewport so a tall body (a long essay at high zoom)
          // scrolls inside the dialog instead of pushing the title and the
          // footer buttons off-screen, where nothing can scroll to them.
          "flex max-h-[calc(100dvh-2rem)] flex-col",
          "rounded-lg border border-neutral-800 bg-neutral-950 shadow-lg",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in data-[state=closed]:fade-out",
          "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          className,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-800 px-5 py-4">
          <div className="min-w-0">
            <Title className="text-base font-semibold text-neutral-100">
              {title}
            </Title>
            {description ? (
              <Description className="mt-1 text-xs text-neutral-400">
                {description}
              </Description>
            ) : null}
          </div>
          <Close
            aria-label="Close dialog"
            className="rounded-md p-1 text-neutral-400 hover:bg-neutral-900 hover:text-neutral-100 focus-visible:outline-2 focus-visible:outline-accent-500"
          >
            <X size={16} />
          </Close>
        </header>
        <div className="min-h-0 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-neutral-800 px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </Content>
    </Portal>
  );
}

// Wrap Root rather than Object.assign onto it: Sheet uses the same Radix
// Root, and assigning onto the shared function let whichever module loaded
// last own `.Content` — Dialog.Content rendered as a Sheet with no header or
// footer, so the /review editor had no Save button.
function DialogRoot(props: ComponentProps<typeof Root>) {
  return <Root {...props} />;
}

export const Dialog = Object.assign(DialogRoot, {
  Trigger,
  Content: DialogContent,
  Close,
});
